import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { SearchTextField } from '../../components/common';
import { useTenant } from '../../contexts/TenantContext';
import RoleForm from './RoleForm';
import RoleUsersDialog from './RoleUsersDialog';
import { useCustomAlert, useErrorToast } from '../../components/common';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { getApiErrorMessage } from '../../utils/errorMessages';

const RolesPage = () => {
  const api = useApi();
  const apiRef = useRef(api);
  const { isJLLUser } = useTenant();
  const { showAlert, showConfirm } = useCustomAlert();
  const { showErrorToast } = useErrorToast();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { searchInput, setSearchInput, searchTerm, clearSearch } = useDebouncedSearch();

  const [formOpen, setFormOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRef.current.get('/roles');
      const apiResponse = response.data;

      if (apiResponse?.success && apiResponse?.data?.roles) {
        setRoles(apiResponse.data.roles);
      } else {
        setRoles([]);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load roles'));
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, []);

  const handleCreate = () => {
    setEditingRole(null);
    setFormOpen(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormOpen(true);
  };

  const handleDelete = async (role) => {
    if (role.userCount > 0) {
      showAlert(`Cannot delete role "${role.roleName}". ${role.userCount} user(s) are assigned. Remove all users first.`, { severity: 'warning' });
      return;
    }

    const confirmed = await showConfirm(`Delete role "${role.roleName}"?`, { title: 'Delete Role', confirmText: 'Delete' });
    if (!confirmed) return;

    try {
      await apiRef.current.delete(`/roles/${role.roleId}`);
      loadRoles();
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, 'Failed to delete role'));
    }
  };

  const handleViewUsers = (role) => {
    setSelectedRole(role);
    setUsersDialogOpen(true);
  };

  const handleFormClose = (refresh = false) => {
    setFormOpen(false);
    setEditingRole(null);
    if (refresh) loadRoles();
  };

  const handleUsersDialogClose = () => {
    setUsersDialogOpen(false);
    setSelectedRole(null);
    loadRoles();
  };

  const q = (searchTerm || '').trim().toLowerCase();
  const filteredRoles = !q
    ? roles
    : roles.filter(
        (role) =>
          (role.roleName || '').toLowerCase().includes(q) || (role.roleCode || '').toLowerCase().includes(q)
      );

  if (!isJLLUser()) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          Role Management is only available for JLL Super Admin users.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              fontWeight: 600,
            }}
          >
            Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage roles and permissions
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadRoles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
          >
            Create Role
          </Button>
        </Box>
      </Box>

      <Box mb={3}>
        <SearchTextField
          placeholder="Search roles..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onClear={clearSearch}
          aria-label="Search roles"
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={1} sx={{ borderRadius: '8px' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Role Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Permissions</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Users</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        {searchTerm.trim() ? 'No roles found matching your search.' : 'No roles found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRoles.map((role) => (
                    <TableRow key={role.roleId} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <SecurityIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {role.roleName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {role.roleCode}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={role.permissionCount} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        {role.userCount > 0 ? (
                          <Chip
                            label={role.userCount}
                            size="small"
                            color="info"
                            onClick={() => handleViewUsers(role)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <Chip label="0" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Users">
                          <IconButton size="small" onClick={() => handleViewUsers(role)}>
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Role">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(role)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={role.userCount > 0 ? 'Remove users first' : 'Delete Role'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(role)}
                              disabled={role.userCount > 0}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <RoleForm open={formOpen} role={editingRole} onClose={handleFormClose} />
      <RoleUsersDialog open={usersDialogOpen} role={selectedRole} onClose={handleUsersDialogClose} />
    </Box>
  );
};

export default RolesPage;
